import { languagesCodesToString } from '../../constants/languages'
import {
  addLanguageKreaEducationalContent,
  associateAuthorsToKreaContent,
  createEducationalContent,
  deleteEducationalContent,
  editEducationalContent,
  editEducationalContentResourcesOrder,
  editEducationalContentType,
  getEducationalContent,
  getEducationalContentCompleteness,
  getEducationalContentLanguageCompleteness,
  getEducationalContentResources,
  searchKreaContentAuthors,
  setEducationalContentKeywords,
  setEducationalContentStatus
} from '../../services/KreaEducationalContent'
import { isError } from '../../utils/api'
import debounce from 'lodash.debounce'

export default {
  state: {
    isSaving: false,
    savingError: false,
    content: null,
    contentCompleteness: null,
    contentLanguageCompleteness: null,
    contentResources: {
      totalCount: 0,
      resources: []
    },
    searchedAuthors: [],
    activeLanguage: null
  },
  mutations: {
    hydrateKreaEducationalContent(state, content) {
      state.content = content
      state.activeLanguage = languagesCodesToString[content.localizedEducationalPrograms[0].locale]
    },
    hydrateKreaEducationalContentCompleteness(state, content) {
      state.contentCompleteness = content
    },
    hydrateKreaEducationalContentResources(state, data) {
      state.contentResources.resources = [...state.contentResources.resources, ...data.data]
      state.contentResources.totalCount = data.totalCount
    },
    hydrateKreaEducationalContentLanguageCompleteness(state, data) {
      state.contentLanguageCompleteness = data
    },
    clearKreaEducationalContentResources(state) {
      state.contentResources.totalCount = 0
      state.contentResources.resources = []
    },
    clearKreaEducationalContent(state) {
      state.content = null
      state.contentCompleteness = null
      state.activeLanguage = null
    },
    setKreaActiveContentLanguage(state, locale) {
      state.activeLanguage = locale
    },
    editKreaLocalizedContentField(state, { field, value, locale }) {
      state.content.localizedEducationalPrograms = state.content.localizedEducationalPrograms.map(pr => {
        return languagesCodesToString[pr.locale] === locale ? ({ ...pr, [field]: value }) : pr
      })
    },
    editKreaContentField(state, { field, value }) {
      state.content[field] = value
    },
    editKreaCustomContentField(state, { fieldId, newOptions }) {
      if (Array.isArray(state.content.customKreaFieldOptions)) {
        state.content.customKreaFieldOptions = [
          ...state.content.customKreaFieldOptions.filter(op => op.customKreaFieldId !== fieldId),
          ...newOptions
        ]
      } else {
        state.content.customKreaFieldOptions = newOptions
      }
    },
    setSearchedAuthors(state, data) {
      state.searchedAuthors = [
        ...data.associatedAuthors.map(author => ({ ...author, associatedPreviously: true })),
        ...data.availableAuthors
      ]
    },
    setSavingContent(state, { saving, error }) {
      state.isSaving = saving
      state.savingError = error
    }
  },
  actions: {
    async createKreaEducationalContent({ dispatch }, contentData) {
      const response = await createEducationalContent(contentData).catch(e => e.response)
      const success = !isError(response.status)
      if (success) {
        await dispatch('createKreaResourceByContent', { locale: contentData.locale, contentId: response.data.id })
      }
      return { success, id: response.data.id }
    },
    async hydrateKreaEducationalContent({ commit }, id) {
      const response = await getEducationalContent(id).catch(e => e.response)
      const success = !isError(response.status)
      if (success) commit('hydrateKreaEducationalContent', response.data)
      return success
    },
    async hydrateKreaEducationalContentCompleteness({ commit }, { id, locale }) {
      const response = await getEducationalContentCompleteness({ id, locale }).catch(e => e.response)
      const success = !isError(response.status)
      if (success) commit('hydrateKreaEducationalContentCompleteness', response.data)
      return success
    },
    async hydrateKreaEducationalContentLanguageCompleteness({ commit }, id) {
      const response = await getEducationalContentLanguageCompleteness(id).catch(e => e.response)
      const success = !isError(response.status)
      if (success) commit('hydrateKreaEducationalContentLanguageCompleteness', response.data)
      return success
    },
    async updateKreaEducationalContent({ commit, state, rootState }) {
      commit('setSavingContent', { saving: true, error: false })
      const response = await editEducationalContent({
        locale: state.activeLanguage,
        id: state.content.id,
        data: {
          ...state.content,
          modifiedBy: rootState.userInfo.userInfo.userId,
          udlIds: state.content.udLs.map(udl => udl.id),
          customKreaFieldOptionIds: state.content.customKreaFieldOptions
            ? state.content.customKreaFieldOptions.filter(op => op.id).map(op => op.id)
            : null
        }
      }).catch(e => e.response)
      const success = !isError(response.status)
      commit('setSavingContent', { saving: false, error: !success })
      return success
    },
    async addLanguageKreaEducationalContent({ commit }, { id, locale }) {
      const response = await addLanguageKreaEducationalContent({ locale, id }).catch(e => e.response)
      const success = !isError(response.status)
      return success
    },
    async editKreaEducationalContentType({ commit }, { id, contentType }) {
      const response = await editEducationalContentType({ contentType, id }).catch(e => e.response)
      const success = !isError(response.status)
      return success
    },
    async hydrateKreaEducationalContentResources({ commit }, { id, pageNumber, pageSize, locale }) {
      const response = await getEducationalContentResources({ id, pageNumber, pageSize, locale }).catch(e => e.response)
      const success = !isError(response.status)
      if (success) commit('hydrateKreaEducationalContentResources', response.data)
      return success
    },
    async updateKreaResourcesContentOrder({ state }, resources) {
      const response = await editEducationalContentResourcesOrder({ id: state.content.id, resources }).catch(e => e.response)
      const success = !isError(response.status)
      return success
    },
    async setKreaEducationalContentKeywords({ commit }, { keywords, id }) {
      const response = await setEducationalContentKeywords({ keywords, id }).catch(e => e.response)
      const success = !isError(response.status)
      return { success, error: response.data }
    },
    async setKreaEducationalContentStatus({ commit }, { id, status, locales }) {
      const response = await setEducationalContentStatus({ status, id, locales }).catch(e => e.response)
      const success = !isError(response.status)
      return { success, error: response.data }
    },
    async deleteKreaEducationalContent({ commit }, { id, locale }) {
      const response = await deleteEducationalContent({ id, locale }).catch(e => e.response)
      const success = !isError(response.status)
      return success
    },
    async searchKreaContentAuthors({ commit }, filters = {}) {
      const response = await searchKreaContentAuthors(filters).catch(e => e.response)
      const successfulRequest = !isError(response.status)
      if (successfulRequest) commit('setSearchedAuthors', response.data)
      return successfulRequest
    },
    async associateAuthorsToKreaContent({ commit }, { id, authors, url }) {
      const response = await associateAuthorsToKreaContent({ id, authors, url }).catch(e => e.response)
      const success = !isError(response.status)
      return { success, data: response.data }
    },
    clearKreaEducationalContentResources({ commit }) {
      commit('clearKreaEducationalContentResources')
    },
    clearKreaEducationalContent({ commit }) {
      commit('clearKreaEducationalContent')
    },
    debounceKreaEducationalContentUpdate: debounce(({ dispatch }) => {
      dispatch('updateKreaEducationalContent')
    }, 2000),
    setKreaActiveContentLanguage({ commit }, locale) {
      commit('setKreaActiveContentLanguage', locale)
    },
    editKreaLocalizedContentField({ commit, dispatch }, { field, value, locale }) {
      commit('editKreaLocalizedContentField', { field, value, locale })
      dispatch('debounceKreaEducationalContentUpdate')
    },
    editKreaContentField({ commit, dispatch }, { field, value }) {
      commit('editKreaContentField', { field, value })
      dispatch('debounceKreaEducationalContentUpdate')
    },
    editKreaCustomContentField({ commit, dispatch }, { fieldId, newOptions }) {
      commit('editKreaCustomContentField', { fieldId, newOptions })
      dispatch('debounceKreaEducationalContentUpdate')
    }
  }
}
